import { Suspense } from "react";
import { Outlet } from "react-router";

import useAuth from "~/hooks/auth/useAuth";
import useClientRedirect from "~/hooks/useClientRedirect";

import RocketRentLogo from "./RocketRentLogo";

const CheckIcon = () => (
  <svg
    className="h-5 w-5 flex-shrink-0 text-blue-600"
    fill="currentColor"
    viewBox="0 0 20 20"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
      clipRule="evenodd"
    />
  </svg>
);

const LayoutAuthBeta = () => {
  const clientRedirect = useClientRedirect();
  const { isInitialLoad, isLoading, isAuthenticated, user } = useAuth();

  if (isInitialLoad && isLoading) {
    return null;
  }

  if (!isLoading && isAuthenticated && user?.internalRole) {
    if (user.internalRole === "landlord") {
      clientRedirect("/landlord");
    }

    if (user.internalRole === "tenant") {
      clientRedirect("/tenant/leases");
    }

    return null;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header - Logo and Badge */}
      <div className="flex flex-col items-center px-6 pt-8 pb-4">
        <Suspense>
          <RocketRentLogo />
        </Suspense>

        <span className="mt-4 inline-block rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
          Beta Program
        </span>
      </div>

      {/* Main Content */}
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col md:mb-20 lg:flex-row">
        {/* Copy Section - Left on desktop */}
        <div className="flex flex-col justify-center px-6 lg:w-1/2 lg:px-12">
          <h1 className="text-2xl font-bold text-gray-900">
            RocketRent Beta Tester Application
          </h1>

          <p className="mt-4 text-gray-600">
            We're opening limited access to RocketRent for early beta users.
          </p>

          <p className="mt-4 text-gray-600">
            RocketRent is a simple property management platform built for
            independent landlords who self-manage. Our beta is for landlords who
            want cleaner leases, simpler rent collection, and fewer moving parts
            than traditional property management software.
          </p>

          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900">
              As a beta tester, you'll get:
            </h2>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Early access to RocketRent features</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>A direct line to the product team</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>The ability to influence what we build next</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Preferential pricing when we exit beta</span>
              </li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900">
              In return, we ask for:
            </h2>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Honest feedback on what works and what doesn't</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>
                  Willingness to test features in real rental scenarios
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Occasional feedback via short surveys or calls</span>
              </li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900">
              This beta is best suited for:
            </h2>
            <ul className="mt-3 space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Self-managing landlords</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Owners with 1–50 units</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>People who value simplicity over complexity</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Form Section - Right on desktop */}
        <div className="flex items-center justify-center bg-gray-50 px-6 py-12 lg:w-1/2 lg:px-12">
          <div className="w-full max-w-md rounded-lg bg-white px-6 py-8 shadow-xl sm:px-8">
            <Suspense>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutAuthBeta;
